import { useState, type FormEvent } from "react";
import { useSession } from "../hooks";
import { navigate } from "../router";
import { RecipientPicker, sendTo, type To } from "./RecipientPicker";
import { Card, Form, Page } from "./ui";
import { t } from "../i18n";

// ComposeView writes a new message to anyone: one or more people, for a
// direct message, or a channel one is in.
export function ComposeView() {
  const session = useSession();
  const [to, setTo] = useState<To[]>([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!to.length || !text.trim()) return;
    setError("");
    setSending(true);
    try {
      navigate(`/c/${await sendTo(session, to, text.trim())}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSending(false);
    }
  }

  return (
    <Page className="compose-view" narrow title={t("New message")} description={t("To one or more people, or to a channel you are in.")}>
      <Card>
        <Form onSubmit={send}>
          <RecipientPicker to={to} onChange={setTo} autoFocus />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder={to.length ? t("Message {names}", { names: to.map((r) => r.label).join(t(", ")) }) : t("Write your message")}
            aria-label={t("Message")}
            rows={5}
          />
          {error && <p className="error">{error}</p>}
          <div className="form-actions">
            <button type="submit" disabled={sending || !to.length || !text.trim()}>
              {sending ? t("Sending…") : t("Send")}
            </button>
          </div>
        </Form>
      </Card>
    </Page>
  );
}
